import { useState, useSyncExternalStore } from 'react'
import { Button, Dropdown, Input, Modal, Switch, message } from 'antd'
import { formatTestIssuePackageMarkdown, formatTestIssuePackageJson, buildTestIssuePackage } from './test-issue-package'
import { testSessionStore } from './test-session-store'
import { getIssueBusinessContext } from '../diagnostics/issue-package-context'
import { endTestSession, startTestSession } from './test-session-controls'
import { MAX_REPLAY_TRANSFER_BYTES, parseReplayTransfer, serializeReplayTransfer } from './replay-transfer'
import VisualReplayModal from './VisualReplayModal'
import { visualReplayStore } from './visual-replay-store'
import { buildTestSessionTimeline, getActionDescription, getRequestDescription } from './test-session-timeline'
import type { TestSessionDiagnostic, TestSessionSnapshot } from './test-session-types'

function formatTimelineTime(at: number): string {
  const date = new Date(at)
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}.${String(date.getMilliseconds()).padStart(3, '0')}`
}

function getDiagnosticDescription(diagnostic: TestSessionDiagnostic): string {
  const kind = diagnostic.kind === 'request' ? '请求异常' : '运行时异常'
  return `${kind}：${diagnostic.error.message}`
}

function getContextSummary(snapshot: TestSessionSnapshot): string[] {
  const context = snapshot.context
  return [
    context?.systemCode ? `系统：${context.systemCode}` : undefined,
    context?.userAccount ? `账号：${context.userAccount}` : undefined,
    context?.roles?.length ? `角色：${context.roles.join('、')}` : undefined,
    snapshot.build ? `构建：${snapshot.build.commit} (${snapshot.build.mode})` : undefined,
    snapshot.initialRoute ? `页面：${snapshot.initialRoute}` : undefined,
  ].filter((item): item is string => Boolean(item))
}

export default function TestSessionPanel() {
  const snapshot = useSyncExternalStore(
    (listener) => testSessionStore.subscribe(listener),
    () => testSessionStore.getSnapshot(),
    () => testSessionStore.getSnapshot()
  )
  const replay = useSyncExternalStore(
    (listener) => visualReplayStore.subscribe(listener),
    () => visualReplayStore.getSnapshot(),
    () => visualReplayStore.getSnapshot()
  )
  const [problemOpen, setProblemOpen] = useState(false)
  const [replayOpen, setReplayOpen] = useState(false)
  const [replayOffset, setReplayOffset] = useState<number | undefined>()
  const [expected, setExpected] = useState('')
  const [actual, setActual] = useState('')
  const [importOpen, setImportOpen] = useState(false)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importedReplay, setImportedReplay] = useState<{
    events: typeof replay.events
    stoppedByLimit: boolean
  } | null>(null)
  const [importedSession, setImportedSession] = useState<TestSessionSnapshot | null>(null)
  const displaySnapshot = importedSession ?? snapshot
  const timeline = buildTestSessionTimeline(displaySnapshot)
  const recentDiagnostics = displaySnapshot.diagnostics?.slice(-3) ?? []
  const contextSummary = getContextSummary(displaySnapshot)
  const displayReplay = importedReplay ?? replay
  const openReplayAt = (at?: number) => {
    const firstEvent = displayReplay.events[0]
    setReplayOffset(typeof at === 'number' && firstEvent ? Math.max(0, at - firstEvent.timestamp) : undefined)
    setReplayOpen(true)
  }
  const copy = async (format: 'markdown' | 'json') => {
    if (!navigator.clipboard) return
    const pkg = buildTestIssuePackage(displaySnapshot, displaySnapshot.context ?? getIssueBusinessContext(displaySnapshot.currentRoute ?? window.location.pathname), { eventCount: displayReplay.events.length, stoppedByLimit: displayReplay.stoppedByLimit })
    await navigator.clipboard.writeText(format === 'json' ? formatTestIssuePackageJson(pkg) : formatTestIssuePackageMarkdown(pkg))
  }
  const exportReplayPackage = async () => {
    if (!snapshot.endedAt || replay.status !== 'ready' || replay.events.length === 0) return

    try {
      const content = await serializeReplayTransfer({
        session: snapshot,
        replay: { events: replay.events, stoppedByLimit: replay.stoppedByLimit },
      })
      const blob = new Blob([content], { type: 'application/json;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `test-session-replay-${new Date().toISOString().replaceAll(':', '-')}.nsy-replay.json`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (error) {
      message.error(error instanceof Error ? error.message : '回放包导出失败')
    }
  }
  const importReplayPackage = async () => {
    if (!importFile) return

    try {
      if (importFile.size > MAX_REPLAY_TRANSFER_BYTES) {
        throw new Error('回放包超过 50 MiB，无法导入')
      }
      const transfer = await parseReplayTransfer(await importFile.text())
      setImportedReplay(transfer.replay)
      setImportedSession(transfer.session)
      setImportFile(null)
      setImportOpen(false)
      setReplayOpen(true)
      message.success('回放包已导入')
    } catch (error) {
      message.error(error instanceof Error ? error.message : '回放包导入失败')
    }
  }
  const markProblem = () => {
    testSessionStore.markProblem({ expected: expected.trim(), actual: actual.trim() })
    setProblemOpen(false)
    setExpected('')
    setActual('')
  }

  return <section className="rounded bg-white p-3 shadow-sm" data-c="TestSessionPanel">
      <div className="mb-3 flex items-center justify-between gap-3">
      <div><div className="text-sm font-medium text-gray-800">测试会话</div><div className="mt-0.5 text-xs text-gray-400">仅在手动开始后记录本次测试操作与请求</div></div>
      <Switch
        aria-label="测试记录"
        checked={snapshot.active}
        checkedChildren="记录中"
        unCheckedChildren="未开始"
        onChange={(checked) => {
          if (checked) {
            setImportedReplay(null)
            setImportedSession(null)
            startTestSession()
            return
          }
          endTestSession()
        }}
      />
    </div>
    {importedSession ? <div className="mb-2 text-xs font-medium text-sky-600">导入会话 · 只读</div> : null}
    {contextSummary.length ? <div data-testid="test-session-context" className="mb-3 rounded bg-gray-50 px-2 py-1.5 text-xs leading-5 text-gray-500">
      {contextSummary.map((item) => <div key={item}>{item}</div>)}
    </div> : null}
    <div className="mt-3 border-t border-gray-100 pt-3">
      <div className="mb-2 flex items-center justify-between gap-2" data-testid="test-session-record-header">
        <div className="text-xs font-medium text-gray-700">操作记录</div>
        <div className="flex items-center gap-2 text-xs whitespace-nowrap">
          <span className="text-gray-400">操作 {displaySnapshot.actions.length}</span>
          <span className="text-gray-400">请求 {displaySnapshot.requests.length}</span>
        </div>
      </div>
      {timeline.length ? <div className="max-h-80 space-y-1.5 overflow-y-auto pr-1" data-testid="test-session-record-list">
        {timeline.map((entry) => {
          if (entry.type === 'action') return <article key={`action-${entry.at}-${entry.action.label ?? ''}`} className="space-y-1.5 border-b border-gray-100 pb-1.5 last:border-b-0 last:pb-0">
            <div className="grid grid-cols-[84px_minmax(0,1fr)] gap-1 text-xs">
              <time className="w-[84px] shrink-0 text-gray-400">{formatTimelineTime(entry.action.at)}</time>
              <span className="min-w-0 break-words text-gray-700">{getActionDescription(entry.action)}</span>
            </div>
            {entry.requests.map((request) => <div key={`request-${request.at}-${request.method}-${request.path}`} className="grid grid-cols-[84px_minmax(0,1fr)] gap-1 text-xs">
                <time className="w-[84px] shrink-0 pt-1 text-gray-400">{formatTimelineTime(request.at)}</time>
                <div className="min-w-0 rounded bg-gray-50 px-2 py-1.5">
                  <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                    <span className={request.businessOutcome === 'failure' || request.outcome === 'failure' ? 'font-medium text-red-600' : 'font-medium text-emerald-600'}>
                      {request.businessOutcome === 'failure' ? `业务失败${request.responseCode ? ` ${request.responseCode}` : ''}` : `${request.outcome === 'success' ? '成功' : '失败'} ${request.status ?? '未知'}`}
                    </span>
                    <span className="text-gray-600">{request.businessOutcome === 'failure' ? `HTTP ${request.status ?? '未知'} · ` : ''}{request.method}{typeof request.durationMs === 'number' ? ` · ${request.durationMs}ms` : ''}</span>
                  </div>
                  <div className="mt-0.5 break-all leading-4 text-gray-500">{request.path}</div>
                  {request.businessOutcome === 'failure' && request.responseMessage ? <div className="mt-0.5 break-words leading-4 text-red-600">{request.responseMessage}</div> : null}
                </div>
            </div>)}
          </article>
          if (entry.type === 'request') return <article key={`background-request-${entry.at}-${entry.request.method}-${entry.request.path}`} className="border-b border-gray-100 pb-2 last:border-b-0 last:pb-0">
          <div className="grid grid-cols-[84px_minmax(0,1fr)] gap-1 text-xs">
            <time className="w-[84px] shrink-0 text-gray-400">{formatTimelineTime(entry.request.at)}</time>
            <div className="min-w-0 rounded bg-gray-50 px-2 py-1.5">
              <div className="text-gray-700">后台请求：{getRequestDescription(entry.request)}</div>
            </div>
          </div>
          </article>
          if (entry.type === 'diagnostic') return <article key={`diagnostic-${entry.diagnostic.id}`} className="border-b border-gray-100 pb-2 last:border-b-0 last:pb-0">
          <div className="grid grid-cols-[84px_minmax(0,1fr)] gap-1 text-xs">
            <time className="w-[84px] shrink-0 text-gray-400">{formatTimelineTime(entry.diagnostic.at)}</time>
            <div className="min-w-0 rounded bg-amber-50 px-2 py-1.5">
              <div className="font-medium text-amber-700">{getDiagnosticDescription(entry.diagnostic)}</div>
              {entry.diagnostic.request ? <div className="mt-0.5 break-all leading-4 text-amber-700/80">{entry.diagnostic.request.status ?? '未知'} · {entry.diagnostic.request.method} {entry.diagnostic.request.path}</div> : null}
            </div>
          </div>
          </article>
          return <article key={`problem-${entry.problem.id}`} className="border-b border-gray-100 pb-2 last:border-b-0 last:pb-0">
            <div className="grid grid-cols-[84px_minmax(0,1fr)] gap-1 text-xs">
              <time className="w-[84px] shrink-0 pt-1 text-gray-400">{formatTimelineTime(entry.problem.at)}</time>
              <div className="min-w-0 rounded bg-gray-50 px-2 py-1.5 text-gray-600" data-testid="test-session-problem-marker">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-blue-600">问题标记：{entry.problem.actual}</span>
                  {displayReplay.events.length ? <Button type="link" size="small" className="!h-auto !px-0 !text-xs" onClick={() => openReplayAt(entry.problem.at)}>定位回放</Button> : null}
                </div>
                <div className="mt-0.5">预期：{entry.problem.expected}</div>
                {entry.problem.action ? <div className="mt-0.5">关联：{getActionDescription(entry.problem.action)}</div> : null}
                {entry.problem.request ? <div className="mt-0.5 break-all">关联请求：{entry.problem.request.method} {entry.problem.request.path}</div> : null}
              </div>
            </div>
          </article>
        })}
      </div> : <div className="py-6 text-center text-xs text-gray-400">暂无操作记录</div>}
      {recentDiagnostics.length ? <div className="mt-2 text-xs text-amber-600">会话异常 {displaySnapshot.diagnostics?.length ?? 0}</div> : null}
      <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-2">
        <div>
          <Button type="link" size="small" disabled={Boolean(importedSession) || (!snapshot.actions.length && !snapshot.requests.length)} onClick={() => setProblemOpen(true)} className="!px-0">标记问题</Button>
        </div>
        <div className="flex items-center gap-3">
          <Dropdown
            trigger={['hover']}
            menu={{
              items: [
                { key: 'view', label: '查看回放', disabled: displayReplay.events.length === 0 },
                { key: 'export', label: '导出当前回放', disabled: !snapshot.endedAt || replay.status !== 'ready' || replay.events.length === 0 },
                { type: 'divider' },
                { key: 'import', label: '导入回放' },
              ],
              onClick: ({ key }) => {
                if (key === 'view') {
                  openReplayAt()
                  return
                }
                if (key === 'import') {
                  setImportOpen(true)
                  return
                }
                void exportReplayPackage()
              },
            }}
          >
            <Button type="link" size="small" aria-label="回放" className="!px-0">回放</Button>
          </Dropdown>
          <Dropdown
            trigger={['hover']}
            menu={{
              items: [
                { key: 'markdown', label: '复制 MD' },
                { key: 'json', label: '复制 JSON' },
              ],
              onClick: ({ key }) => {
                void copy(key === 'json' ? 'json' : 'markdown')
              },
            }}
          >
            <Button type="link" size="small" aria-label="复制记录" disabled={!displaySnapshot.endedAt} className="!px-0">复制记录</Button>
          </Dropdown>
        </div>
      </div>
    </div>
    <Modal rootClassName="devtools-test-session-modal rr-block" title="标记功能问题" open={problemOpen} onCancel={() => setProblemOpen(false)} onOk={markProblem} okText="确认标记" okButtonProps={{ disabled: !expected.trim() || !actual.trim() }}>
      <p className="mb-3 text-sm text-gray-500">标记会随本次测试记录保存，后续复制记录时会一并带出。</p>
      <label className="mb-1 block text-sm text-gray-700" htmlFor="test-session-expected">预期结果</label><Input.TextArea id="test-session-expected" value={expected} onChange={(event) => setExpected(event.target.value)} />
      <label className="mb-1 mt-3 block text-sm text-gray-700" htmlFor="test-session-actual">实际结果</label><Input.TextArea id="test-session-actual" value={actual} onChange={(event) => setActual(event.target.value)} />
    </Modal>
    <Modal
      title="导入回放包"
      rootClassName="devtools-test-session-modal rr-block"
      open={importOpen}
      okText="导入并查看"
      cancelText="取消"
      destroyOnHidden
        okButtonProps={{ disabled: !importFile }}
        onCancel={() => {
          setImportFile(null)
          setImportOpen(false)
        }}
        onOk={() => void importReplayPackage()}
      >
      <label className="mb-2 block text-sm text-gray-700" htmlFor="replay-transfer-file">选择回放包</label>
      <Input
        id="replay-transfer-file"
        type="file"
        accept=".nsy-replay.json,application/json"
        onChange={(event) => setImportFile(event.target.files?.[0] ?? null)}
      />
    </Modal>
    <VisualReplayModal open={replayOpen} events={displayReplay.events} session={displaySnapshot} stoppedByLimit={displayReplay.stoppedByLimit} initialTimeOffset={replayOffset} onClose={() => setReplayOpen(false)} />
  </section>
}
